import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import uniqBy from 'lodash.uniqby';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { useTranslation } from '@hooks';
import { Completeness } from '@constants/companies';
import { FiltersIcon } from 'assets/images/icons';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const countActiveFilters = (value, options) =>
    Number(value.brands.length !== options.brands.length) +
    Number(value.completenessLevels.length !== options.completenessLevels.length);

const FiltersPanel = ({ onApplyFilters, filter }) => {
    const [isFilterOpened, setFilterOpened] = useState(false);
    const [draft, setDraft] = useState(filter);

    const companies = useSelector(state => state.account?.account?.companies || []);

    const { t } = useTranslation();

    const options = useMemo(() => {
        const completenessLevelsOptions = [
            { value: Completeness.Critical, label: t('common.critical') },
            { value: Completeness.Low, label: t('common.low') },
            { value: Completeness.Average, label: t('common.average') },
            { value: Completeness.Good, label: t('common.good') },
            { value: Completeness.Excellent, label: t('common.excellent') },
        ];

        const locationBrandsOptions = uniqBy(companies, 'brand.id')
            .filter(company => company.brand)
            .map(item => ({
                value: item.brand.id,
                label: item.brand.title,
            }));

        return {
            brands: [...locationBrandsOptions, { value: null, label: `(${t('common.none')})` }],
            completenessLevels: completenessLevelsOptions,
        };
    }, [companies]);

    const filterNumber = countActiveFilters(filter, options);

    const openModal = () => {
        setDraft(filter);
        setFilterOpened(true);
    };

    const formOptions = [
        {
            type: 'multiSelect',
            label: t('filters.brands'),
            value: draft.brands,
            onChange: value => setDraft(prev => ({ ...prev, brands: value })),
            options: options.brands,
            countable: true,
            error: !draft.brands.length,
        },
        {
            type: 'multiSelect',
            label: t('SettingsCompanies.optimizationScore'),
            value: draft.completenessLevels,
            onChange: value => setDraft(prev => ({ ...prev, completenessLevels: value })),
            options: options.completenessLevels,
            countable: true,
            error: !draft.completenessLevels.length,
        },
    ];

    const isValid = draft.brands.length > 0 && draft.completenessLevels.length > 0;

    const resetFilters = () =>
        setDraft({
            brands: options.brands.map(option => option.value),
            completenessLevels: options.completenessLevels.map(option => option.value),
        });

    return (
        <>
            <Button
                sx={{
                    minWidth: '40px',
                    '& .MuiButton-startIcon svg': {
                        width: 'auto',
                        height: 'auto',
                    },
                }}
                variant="outlined"
                onClick={openModal}
                startIcon={<FiltersIcon />}
            >
                {t('buttons.filters')}
                {filterNumber > 0 ? ` (${filterNumber})` : ''}
            </Button>
            <FiltersModal
                isOpen={isFilterOpened}
                handleClose={() => setFilterOpened(false)}
                onApply={() => {
                    onApplyFilters(draft);
                    setFilterOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={countActiveFilters(draft, options) === 0}
            >
                <FiltersFormCreator type="locations" options={formOptions} />
            </FiltersModal>
        </>
    );
};

export default FiltersPanel;
